import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import classNames from 'classnames';
import { $getRoot, EditorState, ParagraphNode } from 'lexical';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { BadgeNode } from './node/BadgeNode';
import { ParameterDto } from '@/Domain/api/Action/dto/Action.dto';
import { LineGutter } from '@/Domain/components/Action/Lexical/component/LineGutter';
import { InitFromFormPlugin } from '@/Domain/components/Action/Lexical/plugin/InitFromFormPlugin';
import { InsertBadgePlugin } from '@/Domain/components/Action/Lexical/plugin/InsertBadgePlugin';
import JsonSyntaxHighlightPlugin from '@/Domain/components/Action/Lexical/plugin/JsonSyntaxHighlightPlugin';

type Props = {
  field: ControllerRenderProps<any, string>;
  parameters?: ParameterDto[];
};

export const JsonEditor = ({ field, parameters }: Props) => {
  const config = useMemo<InitialConfigType>(
    () => ({
      namespace: 'json-badge-editor',
      theme: {
        paragraph: 'm-0 leading-6',
      },
      onError: (error: Error) => console.error('Lexical error:', error?.message || error),
      nodes: [BadgeNode],
    }),
    [],
  );

  return (
    <LexicalComposer initialConfig={config}>
      <JsonBadgeEditor field={field} parameters={parameters} />
    </LexicalComposer>
  );
};

export const JsonBadgeEditor = ({ field, parameters }: Props) => {
  const [editor] = useLexicalComposerContext();
  const [error, setError] = useState(false);
  const [lineCount, setLineCount] = useState(1);
  const lastEmittedRef = useRef<string>('');
  const suppressExternalValueRef = useRef(false);

  useEffect(() => {
    BadgeNode.parameters = parameters || [];
    requestAnimationFrame(() => {
      editor.update(() => {
        const root = $getRoot();
        for (const paragraph of root.getChildren()) {
          if (paragraph.getType() !== 'paragraph') continue;
          for (const node of (paragraph as ParagraphNode).getChildren()) {
            if (node.getType() === 'badge') {
              const badge = node as BadgeNode;
              badge.replace(BadgeNode.cloneWithoutKey(badge));
            }
          }
        }
      });
    });
  }, [parameters, editor]);

  const handleChange = (editorState: EditorState) => {
    let nextValue = '';
    let isValid = true;

    editorState.read(() => {
      const root = $getRoot();

      const lines: string[] = [];
      const currentParamIds = new Set<number>();

      for (const block of root.getChildren()) {
        if (block.getType() !== 'paragraph') {
          lines.push(block.getTextContent());
          continue;
        }
        const parts: string[] = [];
        for (const node of (block as ParagraphNode).getChildren()) {
          if (node.getType() === 'badge') {
            const badge = node as unknown as BadgeNode;
            parts.push(badge.getLabel());
            if (badge.__parameterId) currentParamIds.add(badge.__parameterId);
          } else {
            parts.push(node.getTextContent());
          }
        }
        lines.push(parts.join(''));
      }

      const serialized = lines.join('\n');
      const normalized = serialized.replace(/\u00a0/g, ' ');
      const replacedForValidation = normalized.replace(/\{\{[^}]+\}\}/g, 'PLACEHOLDER');

      try {
        JSON.parse(replacedForValidation);
        isValid = true;
      } catch {
        isValid = false;
      }

      nextValue = normalized;
    });

    setError(!isValid);
    if (nextValue !== lastEmittedRef.current) {
      lastEmittedRef.current = nextValue;
      suppressExternalValueRef.current = true;

      queueMicrotask(() => {
        field.onChange(nextValue);
        requestAnimationFrame(() => {
          suppressExternalValueRef.current = false;
        });
      });
    }
  };

  return (
    <>
      <div
        className={classNames(
          'relative overflow-hidden rounded-xl border border-input bg-card font-mono text-sm text-foreground outline-none focus-within:!ring-[3px]',
          {
            'focus-within:!border-destructive !border-destructive text-destructive focus-within:!ring-destructive/20':
              error,
            'focus-within:!border-foreground focus-within:ring-ring/25': !error,
          },
        )}
      >
        <div className="grid grid-cols-[auto_1fr] gap-0.5 max-h-[420px] overflow-auto">
          <LineGutter count={lineCount} />
          <div className="min-h-[200px]">
            <RichTextPlugin
              contentEditable={
                <ContentEditable className="whitespace-pre leading-6 text-foreground outline-none" />
              }
              ErrorBoundary={({ children }) => <>{children}</>}
            />
          </div>
        </div>

        {parameters ? (
          <div className="absolute top-2 right-2">
            <InsertBadgePlugin parameters={parameters} />
          </div>
        ) : null}

        <OnChangePlugin onChange={handleChange} />
        <JsonSyntaxHighlightPlugin />
        <TabIndentationPlugin />
        <InitFromFormPlugin
          value={field.value ?? ''}
          parameters={parameters}
          onLineCountChange={setLineCount}
          suppressExternalValueRef={suppressExternalValueRef}
        />
      </div>

      {error && <p className="text-sm font-medium text-destructive">Invalid JSON format</p>}
    </>
  );
};
